import type { DocPage } from '../types'

export const troubleshooting: DocPage = {
  id: 'troubleshooting',
  title: 'Troubleshooting',
  description:
    'Common sign-in, entitlement, backend, mapping, spatial, and transfer issues and what to try next.',
  category: 'Reference',
  sections: [
    { id: 'auth', title: 'Sign-in and session' },
    { id: 'access', title: 'Access and entitlements' },
    { id: 'backends', title: 'Backends and CAD' },
    { id: 'configs', title: 'Configurations and profiles' },
    { id: 'mapping', title: 'Mapping problems' },
    { id: 'spatial', title: 'Map and spatial filters' },
    { id: 'transfer', title: 'Transfer problems' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'auth',
      text: 'Sign-in and session',
    },
    {
      type: 'table',
      headers: ['Symptom', 'What to try'],
      rows: [
        [
          'Sign-in cancelled / Login code missing',
          'Do not close the browser early; complete login within about two minutes; retry Sign In; try Sign in using system browser instead.',
        ],
        [
          'Authorization Failed in the browser',
          'Read the error description from SEPM; confirm you used the correct account; try again.',
        ],
        [
          'Returned to login after restart',
          'Session/refresh token expired — sign in again.',
        ],
        [
          'Invalid or expired access token',
          'Use Refresh Account; if it still fails, sign out and sign in.',
        ],
        [
          'Checking session… never finishes',
          'Wait briefly; if stuck, restart the app; sign in manually when the login card appears.',
        ],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'access',
      text: 'Access and entitlements',
    },
    {
      type: 'table',
      headers: ['Symptom', 'What to try'],
      rows: [
        [
          'Switch organization / no access overlay',
          'Pick an org with X-Translator entitlements; ask admin for a LIVE seat; Refresh Account.',
        ],
        [
          'Organization details unavailable',
          'Refresh Account or sign out/in so org details reload.',
        ],
        [
          'Format locked or greyed out',
          'Your plan does not include that format source/target — contact admin.',
        ],
        [
          'Create Custom Config locked',
          'Need create_custom_config; use New until granted; Refresh Account after grant.',
        ],
        [
          'Transfer History or settings restricted',
          'Missing feature entitlement (view-transfer-history / change-log-settings / coordinate conversion); contact admin.',
        ],
        [
          'Access restricted dialog',
          'Read which setting you opened; ask admin for that feature key; no billing UI inside the app.',
        ],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'backends',
      text: 'Backends and CAD',
    },
    {
      type: 'list',
      items: [
        'Splash stuck before Backends are ready — wait longer; restart the app; ensure no firewall blocks local Python/CAD processes.',
        'AutoCAD file locked — close the drawing in other applications and retry.',
        'Large drawings slow to open — use filters, disable unneeded feature counts, or prefer Translator mode with focused layers.',
        'CAD geometry in the wrong place on the map — set EPSG Code (Override EPSG) on the source form.',
        'CAD backend errors in ERRORS — confirm the CAD service started on splash; restart X-Translator.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'configs',
      text: 'Configurations and profiles',
    },
    {
      type: 'table',
      headers: ['Symptom', 'What to try'],
      rows: [
        [
          'Unsaved Changes / load warning',
          'Save or Save as new configuration before loading another model; or Discard consciously.',
        ],
        [
          'UNSAVED on Transfer Review',
          'Save the model as a configuration; tip: Model needs to save as configuration.',
        ],
        [
          'Duplicate configuration name',
          'Choose a unique Configuration Name or use Save as new configuration.',
        ],
        [
          'Profile switch lost mappings',
          'When prompted, Save and continue / save as new instead of Discard.',
        ],
        [
          'Profile loads but connection fails',
          'Password/host changed — Edit Config, reconnect, Update Profile.',
        ],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'mapping',
      text: 'Mapping problems',
    },
    {
      type: 'list',
      items: [
        'V disabled — you are in a scratch transfer; use Set fixed value instead of value mapping.',
        'Auto Map empty — broaden strategy to Balanced/Exploratory, relax Require compatible types, or select collection pairs first.',
        'Inactive mappings after reload — Remove inactive mapping, then re-Connect or Auto Map.',
        'Connect-ID create-on-no-match disabled — mark at least one Connect-ID attribute mapping first.',
        'ENUM fixed/value issues — define a PostGIS logical enumerator and select it on the attribute.',
        'Unexpected updates/deletes — verify Predicate expression (for example 1 OR 2) and per-collection create/skip settings.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'spatial',
      text: 'Map and spatial filters',
    },
    {
      type: 'list',
      items: [
        'Showing sampled of total — overview is sampled; the drawn filter still applies to transfer.',
        'Collections missing from transfer — check Layers for hidden items; Transfer Review reports hidden exclusions.',
        'Attribute table Failed to load — refresh map, confirm connection, retry Open attribute table.',
        'Wrong inclusion results — confirm Clip vs Intersect vs Within before Transfer.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'transfer',
      text: 'Transfer problems',
    },
    {
      type: 'list',
      items: [
        'Check ERRORS in the bottom logger first; raise log level to DETAILED/DEBUG temporarily if entitled.',
        'Confirm source and target connections still succeed (re-open config if needed).',
        'For mapped transfers, verify collections/attributes are still connected after model changes.',
        'For PostGIS scratch, validate Database / Schema / Table (base).',
        'For Connect-ID updates, verify key attributes, predicate, and create-on-no-match.',
        'Use Transfer History to download logs for a failed run (if entitled).',
        'Reduce scope with collection filters or a spatial filter when working with very large datasets.',
        'Style files missing after success — confirm Export style sidecars / output folder on the Target form.',
      ],
    },
  ],
}
